/**
 * Price-range slider with a "£X – £Y available" caption. The slider only
 * commits values upstream on release (not during drag) so parent refetches
 * aren't triggered on every pixel of mouse movement.
 *
 * Extracted from `app/(main)/products/ProductsClient.tsx` lines 1287–1331
 * (slider) and 1435–1451 (section wrapper).
 */

"use client";

import { useEffect, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { FilterSection } from "./FilterSection";

interface PriceRangeSliderProps {
  rangeMin: number;
  rangeMax: number;
  minPrice: string;
  maxPrice: string;
  onPriceChange: (min: string, max: string) => void;
}

function PriceRangeSlider({
  rangeMin,
  rangeMax,
  minPrice,
  maxPrice,
  onPriceChange,
}: PriceRangeSliderProps) {
  const minVal =
    minPrice !== ""
      ? Math.min(Math.max(parseInt(minPrice, 10) || 0, rangeMin), rangeMax)
      : rangeMin;
  const maxVal =
    maxPrice !== ""
      ? Math.min(Math.max(parseInt(maxPrice, 10) || 0, rangeMin), rangeMax)
      : rangeMax;
  const [liveValue, setLiveValue] = useState<[number, number] | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset transient slider feedback when its controlled filter values change.
    setLiveValue(null);
  }, [minPrice, maxPrice]);
  const value: [number, number] = liveValue ?? [minVal, maxVal];
  return (
    <div className="min-h-12 overflow-hidden">
      <Slider
        min={rangeMin}
        max={rangeMax}
        step={1}
        minStepsBetweenThumbs={1}
        value={value}
        onValueChange={([a, b]) => setLiveValue([a, b])}
        onValueCommit={([a, b]) => {
          onPriceChange(
            a === rangeMin ? "" : String(a),
            b === rangeMax ? "" : String(b)
          );
          setLiveValue(null);
        }}
      />
      <p className="mt-1 text-center text-sm text-muted-foreground">
        £{value[0]} – £{value[1]}
      </p>
    </div>
  );
}

interface PriceRangeSectionProps {
  minPrice: string;
  maxPrice: string;
  onPriceChange: (min: string, max: string) => void;
  priceRangePence: { min: number; max: number } | null;
  isOpen: boolean;
  onToggleSection: (title: string) => void;
}

export function PriceRangeSection({
  minPrice,
  maxPrice,
  onPriceChange,
  priceRangePence,
  isOpen,
  onToggleSection,
}: PriceRangeSectionProps) {
  return (
    <FilterSection
      title="Price range (£)"
      count={minPrice || maxPrice ? 1 : 0}
      isOpen={isOpen}
      onToggle={onToggleSection}
    >
      {priceRangePence && (
        <p className="mb-2 text-xs text-muted-foreground">
          £{(priceRangePence.min / 100).toFixed(0)} – £{(priceRangePence.max / 100).toFixed(0)} available
        </p>
      )}
      <PriceRangeSlider
        rangeMin={priceRangePence ? priceRangePence.min / 100 : 0}
        rangeMax={priceRangePence ? priceRangePence.max / 100 : 9999}
        minPrice={minPrice}
        maxPrice={maxPrice}
        onPriceChange={onPriceChange}
      />
    </FilterSection>
  );
}
